// router.ts — Routage typé du frontend (S7 du plan TS+Vite, mai 2026).
//
// Objectif : éliminer les `parseInt(id || '0')` qui parsemaient chaque page
// handler en exploitant le type des paramètres de route directement à
// l'enregistrement. Le router runtime (`core.ts > router`) reste inchangé,
// on lui ajoute juste une couche typée à l'enregistrement via `defineRoute`.
//
// Usage :
//   import { defineRoute } from '@/router.js';
//
//   defineRoute('project/:id', ({ id }) => {
//     // id est typé `number`, pas `string` — pas besoin de parseInt.
//     router.navigate('#project/' + id + '/revisions');
//   }, 'projects.read');

// ── Mapping pattern → forme typée des params ────────────────────────────
//
// Les clés sont les patterns exacts utilisés par `router.on` dans boot.ts.
// Les valeurs décrivent la forme typée attendue par le handler après
// coercion (string → number sur les ids).
//
// Ajouter une route ici quand on en ajoute une dans boot.ts. TS bloquera
// au compile-time si le pattern est inconnu.

export type Routes = {
  dashboard: Record<string, never>;
  workspace: Record<string, never>;
  'meetings-records': Record<string, never>;
  postits: Record<string, never>;
  search: Record<string, never>;
  actions: Record<string, never>;
  'action-plans': Record<string, never>;
  'action-plan/:id': { id: number };
  'action-plan/:id/phase/:phaseId/kpi': { id: number; phaseId: number };
  bugs: Record<string, never>;
  'bug/:id': { id: number };
  reviews: Record<string, never>;
  'review/:id': { id: number };
  'review/:id/support': { id: number };
  notifications: Record<string, never>;
  programs: Record<string, never>;
  'meeting-support': Record<string, never>;
  'invite-committee': Record<string, never>;
  'user-replacement': Record<string, never>;
  'stats/usage': Record<string, never>;
  'stats/project-synthesis': Record<string, never>;
  'my-quotas': Record<string, never>;
  'my-account': Record<string, never>;

  'admin/committees': Record<string, never>;
  'admin/committees/:id': { id: number };
  'admin/meeting-guides': Record<string, never>;
  'admin/structures': Record<string, never>;
  'admin/generic-roles': Record<string, never>;
  'admin/checklists': Record<string, never>;
  'admin/checklists/:id': { id: number };
  'admin/risk-lists': Record<string, never>;
  'admin/risk-lists/:id': { id: number };
  'admin/stages': Record<string, never>;
  'admin/action-statuses': Record<string, never>;
  'admin/risk-colors': Record<string, never>;
  'admin/users': Record<string, never>;
  'admin/user-groups': Record<string, never>;
  'admin/access-matrix': Record<string, never>;
  'admin/audit-log': Record<string, never>;
  'admin/invitation-points': Record<string, never>;
  'admin/vocabulary': Record<string, never>;
  'admin/tenant-settings': Record<string, never>;
  'admin/mascot': Record<string, never>;

  'project/:id': { id: number };
  'project/:id/revisions': { id: number };
  'program/:id': { id: number };
  'revision/:id': { id: number };
  'matrix/:pid': { pid: number };
  'matrix/project/:pid': { pid: number };
  'matrix/program/:gid': { gid: number };
  'evolution/:pid': { pid: number };
  'evolution/project/:pid': { pid: number };
  'evolution/program/:gid': { gid: number };
  'bascule/:appId': { appId: number };
  'action/:id': { id: number };
  'action/:id/versions': { id: number };
  'actions/merge/:keeperId/:absorbedId': { keeperId: number; absorbedId: number };
  'meeting/:id': { id: number };
};

// ── Coercion runtime ────────────────────────────────────────────────────
//
// Le router (core.ts) extrait des params bruts en `Record<string, string>`.
// `coerceRouteParams` convertit les clés numériques connues (id, pid, gid,
// appId) en `number`. Les autres clés restent string.
//
// Stratégie volontairement simpliste : on n'a pas de DSL pattern → schema,
// le set de noms numériques est petit et explicite. Si on ajoute une route
// avec une nouvelle clé numérique, l'ajouter ici.

const NUMERIC_PARAM_KEYS = new Set([
  'id',
  'pid',
  'gid',
  'appId',
  'keeperId',
  'absorbedId',
  'phaseId',
]);

export function coerceRouteParams<P extends keyof Routes>(raw: Record<string, string>): Routes[P] {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(raw)) {
    out[k] = NUMERIC_PARAM_KEYS.has(k) ? parseInt(v) || 0 : v;
  }
  return out as Routes[P];
}

// ── defineRoute : enregistrement typé d'une route ────────────────────────
//
// Wrapper autour de `router.on()` (core.ts) qui :
//   1. Vérifie que `pattern` est un `keyof Routes` (typo bloqué au compile).
//   2. Type le handler avec les params déjà coercés (`Routes[P]`).
//   3. Coerce automatiquement avant d'appeler le handler.

export function defineRoute<P extends keyof Routes>(
  pattern: P,
  handler: (params: Routes[P]) => void | Promise<void>,
  perm?: string | null,
): void {
  router.on(
    pattern,
    (raw) => {
      const params = coerceRouteParams<P>(raw);
      return handler(params);
    },
    perm ?? null,
  );
}

// ── definePage : typage strict d'un handler de page ─────────────────────
//
// Utilisé dans les modules `page-*.ts` quand on enregistre `pages.X = ...`.
// Le handler est typé selon le pattern (`Routes[P]`) ; au runtime, le wrapper
// applique la coercion string → number avant d'appeler le vrai handler.
//
// Le retour est typé `PageHandler` (signature `Record<string, string>`)
// pour rester rétrocompatible avec le runtime existant et le pattern
// `pages.adminXxx?.({})` utilisé par les CRUD admin pour re-render.
//
// Usage :
//   pages.project = definePage<'project/:id'>(({ id }) => {
//     // id est typé `number`.
//     navigateTo('project/:id/revisions', { id });
//   });

type _PageHandlerCompat = (params: Record<string, string>) => void | Promise<void>;

export function definePage<P extends keyof Routes>(
  handler: (params: Routes[P]) => void | Promise<void>,
): _PageHandlerCompat {
  return (raw) => {
    const params = coerceRouteParams<P>(raw);
    return handler(params);
  };
}

// ── navigateTo : navigation typée ───────────────────────────────────────
//
// Construit un hash valide depuis un pattern + ses params. Évite les
// concaténations à la main type `'#project/' + id`.
//
// Usage :
//   navigateTo('project/:id', { id: 42 });           // → '#project/42'
//   navigateTo('project/:id/revisions', { id: 42 }); // → '#project/42/revisions'

type ParamsOf<P extends keyof Routes> = Routes[P];

export function buildHash<P extends keyof Routes>(pattern: P, params: ParamsOf<P>): string {
  let hash = '#' + (pattern as string);
  for (const [k, v] of Object.entries(params as Record<string, string | number>)) {
    hash = hash.replace(':' + k, String(v));
  }
  return hash;
}

export function navigateTo<P extends keyof Routes>(pattern: P, params: ParamsOf<P>): void {
  router.navigate(buildHash(pattern, params));
}
